// ── Registro único dos tipos de entrega no FRONTEND ──────────────────────────
//
// PORQUÊ ESTE ARQUIVO EXISTE
//
// As mesmas listas viviam triplicadas dentro do próprio frontend: DELIVERY_FEATURE,
// productionFeatureKey, normalizePipelineDeliveryType, VIDEO_FORMATS_WITH_MUSIC,
// OVERLAY_STEP_FORMATS e os rótulos existiam em ViralResearchView.tsx E em
// assistant/shared.tsx (que dizia explicitamente "reproduzidas aqui em vez de
// importadas"), mais listas de vídeo em PostPreviewModal.tsx e AssetGallery.tsx.
// Uma correção em um dos arquivos não alcançava o outro, e o payload de
// /pipeline/start é o MESMO dos dois lados.
//
// ⚠️ SINCRONIA COM O BACKEND — MANUAL, DE PROPÓSITO.
//
// Isto é um ESPELHO de backend/src/services/marketingDeliveryTypes.ts. Não há
// sincronização automática (não há código compartilhado entre os dois projetos), e
// não se inventou uma: o custo de um mecanismo mágico é maior que o de duas listas
// com um aviso. O que existe é o contrato:
//
//   AO ACRESCENTAR OU MUDAR UM TIPO DE ENTREGA, MEXA NOS DOIS ARQUIVOS.
//
// As divergências que a consolidação expôs foram julgadas: as que eram bug estão
// marcadas CORRIGIDO no próprio campo (D5 overlayStep de product_reaction, D7 alias
// single_post); as que são decisão de produto continuam anotadas como DIVERGÊNCIA
// D3 RESOLVIDO: product_reaction ganhou feature propria `product_reaction`, criada com o MESMO
// custo do video_avatar (0,50) — nenhuma conta muda; o que muda e poder ajustar uma sem a outra.

export type DeliveryTypeId =
  | 'post_estatico'
  | 'carrossel'
  | 'video_avatar'
  | 'video_agentico'
  | 'narrado'
  | 'reaction_chromakey'
  | 'reaction_split'
  | 'product_reaction';

export type VideoEngine = 'standard' | 'ultra';

export interface DeliveryTypeConfig {
  id: DeliveryTypeId;
  label: string;
  /** É vídeo: gate de player, proporção 9/16, trilha do Instagram. */
  video: boolean;
  /** Feature de crédito cobrada na produção (espelha DELIVERY_FEATURE do backend). */
  creditFeature: string;
  /** Feature quando o motor Ultra está selecionado. */
  ultraCreditFeature: string | null;
  /** Proporção padrão da prévia. */
  aspect: string;
  /**
   * Tem passo de escolha de overlay no wizard.
   *
   * CORRIGIDO (D5): tem de ser o MESMO conjunto de OVERLAY_CHOICE_DELIVERY_TYPES do
   * backend — video_agentico, video_avatar e product_reaction. O wizard oferecia só os
   * dois primeiros: quem produzia product_reaction pela UI nunca via o passo e caía no
   * default do serviço, mesmo o formato NÃO sendo hookOnly (ele transcreve o vídeo
   * inteiro e leva legenda cinética, como o video_avatar — a escolha muda o MP4).
   * Escondê-lo tirava da pessoa uma escolha que o backend já aceitava.
   */
  overlayStep: boolean;
  /**
   * Roda o PIPELINE DE CENAS (takes + composeBrollMerge) — espelha `agenticoPipeline`
   * do backend. Existe porque o front tinha a MESMA igualdade `=== 'video_agentico'`
   * que o backend já tinha aposentado: sem ela, a regeração por cena pela prévia
   * (PostPreviewModal) ficava indisponível em `narrado`, que roda o mesmo motor.
   */
  agenticoPipeline: boolean;
  /**
   * Precisa de avatar da marca configurado (espelha o `needsAvatar` do fluxo manual
   * do Studio, que mantinha a própria lista e por isso não oferecia `narrado`).
   */
  needsAvatar: boolean;
  /**
   * Tem SUBFORMATO selecionável (spec §3/§8.3): comum | palestrinha | storytelling_visual.
   * Espelha `avatarSubformats` do backend. É CAMPO, não deliveryType — nenhuma lista
   * de formato muda por causa dele.
   */
  avatarSubformats: boolean;
  /**
   * Tem SUBFORMATO DE TELA DIVIDIDA (spec §1/§8.4): video_original | cenas_ia.
   * Espelha `splitSubformats` do backend. É CAMPO, não deliveryType — o card escolhido
   * continua sendo "Reaction Tela Dividida" e nenhuma lista de formato muda por causa dele.
   */
  splitSubformats: boolean;
}

export const DELIVERY_TYPES: Readonly<Record<DeliveryTypeId, DeliveryTypeConfig>> = Object.freeze({
  video_agentico: { id: 'video_agentico', label: 'Vídeo por Cenas IA', video: true, creditFeature: 'video_cena', ultraCreditFeature: 'video_cena_ultra', aspect: '9 / 16', agenticoPipeline: true, needsAvatar: true, avatarSubformats: false, splitSubformats: false, overlayStep: true },
  // FORMATO NARRADO (docs/NOVOS_FORMATOS_SPEC.md §2): narração contínua sobre cenas de IA,
  // sem rosto. Mesmo motor e mesma cobrança do Vídeo por Cenas IA — inclusive o Ultra.
  // overlayStep: true tem de bater com OVERLAY_CHOICE_DELIVERY_TYPES do backend (o teste de
  // espelho compara os dois conjuntos). O formato tem fala, logo a legenda sincronizada é
  // viável; esconder o passo não removeria a legenda, só tiraria a escolha da pessoa (D5).
  narrado: { id: 'narrado', label: 'Narrado (cenas IA)', video: true, creditFeature: 'video_cena', ultraCreditFeature: 'video_cena_ultra', aspect: '9 / 16', agenticoPipeline: true, needsAvatar: true, avatarSubformats: false, splitSubformats: false, overlayStep: true },
  video_avatar: { id: 'video_avatar', label: 'Vídeo Avatar', video: true, creditFeature: 'video_avatar', ultraCreditFeature: null, aspect: '9 / 16', agenticoPipeline: false, needsAvatar: true, avatarSubformats: true, splitSubformats: false, overlayStep: true },
  reaction_chromakey: { id: 'reaction_chromakey', label: 'Reaction Chromakey', video: true, creditFeature: 'reaction_chromakey', ultraCreditFeature: null, aspect: '9 / 16', agenticoPipeline: false, needsAvatar: true, avatarSubformats: false, splitSubformats: false, overlayStep: false },
    // TELA DIVIDIDA (spec §1/§8.4): dois subformatos, um só deliveryType — `video_original`
  // (vídeo do criador em cima, o de sempre) e `cenas_ia` (metade de cima gerada). O
  // subformato muda a FEATURE DE CRÉDITO, e só ela: ver SUBFORMAT_CREDIT_FEATURE.
  reaction_split: { id: 'reaction_split', label: 'Reaction Tela Dividida', video: true, creditFeature: 'reaction_split', ultraCreditFeature: null, aspect: '9 / 16', agenticoPipeline: false, needsAvatar: true, avatarSubformats: false, splitSubformats: true, overlayStep: false },
  // DIVERGÊNCIA PRESERVADA (D3, decisão de preço do usuário): cobra a feature do
  // video_avatar em vez de uma própria, apesar de consumir bem mais provedor.
  product_reaction: { id: 'product_reaction', label: 'Product Reaction', video: true, creditFeature: 'product_reaction', ultraCreditFeature: null, aspect: '9 / 16', agenticoPipeline: false, needsAvatar: true, avatarSubformats: false, splitSubformats: false, overlayStep: true },
  carrossel: { id: 'carrossel', label: 'Carrossel', video: false, creditFeature: 'carrossel', ultraCreditFeature: null, aspect: '4 / 5', agenticoPipeline: false, needsAvatar: false, avatarSubformats: false, splitSubformats: false, overlayStep: false },
  post_estatico: { id: 'post_estatico', label: 'Post Estático', video: false, creditFeature: 'post_estatico', ultraCreditFeature: null, aspect: '1 / 1', agenticoPipeline: false, needsAvatar: false, avatarSubformats: false, splitSubformats: false, overlayStep: false },
});

/** Aliases de `format` → deliveryType do pipeline. Espelha normalizePipelineDeliveryType. */
const PIPELINE_ALIASES: Readonly<Record<string, DeliveryTypeId>> = Object.freeze({
  reel: 'video_avatar',
  tiktok: 'video_avatar',
  video: 'video_avatar',
  carousel: 'carrossel',
  tiktok_carousel: 'carrossel',
  post: 'post_estatico',
  // CORRIGIDO (D7, espelhando o backend): single_post passou a ser normalizado.
  single_post: 'post_estatico',
});

/**
 * Aliases que carregam feature de crédito. Depois de D7 (`single_post` normalizado)
 * nenhum deles sobra sem normalização, então na prática este mapa é a REDE por baixo de
 * productionFeatureKey: ele consulta um valor JÁ normalizado. Mantido porque é o espelho
 * do `creditFeature` dos aliases do backend, que resolve pelo valor CRU (regeração de job
 * antigo grava o alias como veio).
 */
const CREDIT_ALIASES: Readonly<Record<string, DeliveryTypeId>> = Object.freeze({
  post: 'post_estatico',
  single_post: 'post_estatico',
});

export function normalizePipelineDeliveryType(format?: string | null): string {
  const key = String(format ?? '');
  return PIPELINE_ALIASES[key] || key || 'post_estatico';
}

export function deliveryConfig(format?: string | null): DeliveryTypeConfig | null {
  const id = normalizePipelineDeliveryType(format) as DeliveryTypeId;
  return (DELIVERY_TYPES as Record<string, DeliveryTypeConfig>)[id] || null;
}

/** Rótulo humano; tipo desconhecido volta como o próprio valor (comportamento de sempre). */
export function formatLabel(format?: string | null): string {
  return deliveryConfig(format)?.label || String(format || '—');
}

export const FORMAT_LABELS: Record<string, string> = Object.freeze(
  Object.fromEntries(Object.values(DELIVERY_TYPES).map(t => [t.id, t.label])),
) as Record<string, string>;

// ── Menus de formato ─────────────────────────────────────────────────────────
//
// A ORDEM É PARTE DO PRODUTO (é a ordem dos cards na tela), então cada menu lista
// os ids explicitamente em vez de derivar de um filtro.

export interface FormatOption { value: DeliveryTypeId; label: string }

const menu = (ids: DeliveryTypeId[]): FormatOption[] =>
  ids.map(id => ({ value: id, label: DELIVERY_TYPES[id].label }));

/** Todos os formatos (pesquisa viral e brief manual). */
export const FORMATS: FormatOption[] = menu([
  'video_agentico', 'narrado', 'video_avatar', 'reaction_chromakey', 'reaction_split', 'post_estatico', 'carrossel', 'product_reaction',
]);

/**
 * Brief por produto: sem a reaction a vídeo de TERCEIRO (reaction_chromakey).
 *
 * `reaction_split` passa a aparecer (spec §1): aqui ele existe só no subformato
 * `cenas_ia` — não há conteúdo de terceiro para reagir, e a metade de cima é cena gerada.
 * Quem força isso é `splitSubformatOptions('produto')`, não este menu.
 */
export const PRODUCT_FORMATS: FormatOption[] = menu([
  'video_agentico', 'narrado', 'video_avatar', 'reaction_split', 'product_reaction', 'carrossel', 'post_estatico',
]);

/** Brief da marca: mesma regra do produto — tela dividida só no lado IA. */
export const BRAND_FORMATS: FormatOption[] = menu([
  'video_agentico', 'narrado', 'video_avatar', 'reaction_split', 'carrossel', 'post_estatico',
]);

// ── Características ──────────────────────────────────────────────────────────
//
// Todas consultam pelo ID CANÔNICO (sem normalizar), igual aos `.includes()` que
// substituíram; quem precisa normalizar faz isso antes, como já fazia.

export const VIDEO_DELIVERY_TYPES: readonly string[] = Object.freeze(
  Object.values(DELIVERY_TYPES).filter(t => t.video).map(t => t.id),
);

/** Trilha do catálogo do Instagram: SÓ vídeo (foto/carrossel são recusados na criação do post). */
export const VIDEO_FORMATS_WITH_MUSIC: readonly string[] = VIDEO_DELIVERY_TYPES;

/** Formatos com passo de escolha de overlay no wizard. */
export const OVERLAY_STEP_FORMATS: readonly string[] = Object.freeze(
  Object.values(DELIVERY_TYPES).filter(t => t.overlayStep).map(t => t.id),
);

export function isVideoDeliveryType(deliveryType?: string | null): boolean {
  const cfg = (DELIVERY_TYPES as Record<string, DeliveryTypeConfig>)[String(deliveryType ?? '')];
  return !!cfg && cfg.video;
}

export function formatSupportsOverlayStep(format?: string | null): boolean {
  return OVERLAY_STEP_FORMATS.includes(normalizePipelineDeliveryType(format));
}

/** Proporção padrão da prévia; desconhecido cai em 1/1, como antes. */
export function defaultAspect(deliveryType?: string | null): string {
  const cfg = (DELIVERY_TYPES as Record<string, DeliveryTypeConfig>)[String(deliveryType ?? '')];
  return cfg ? cfg.aspect : '1 / 1';
}

/**
 * Feature de crédito da produção (para o modal de custo).
 *
 * Devolve `null` para formato desconhecido — e é de propósito: no FRONT o `null` só
 * esconde o aviso de preço. Quem precisa falhar alto é o backend, onde `null`
 * significaria produção paga rodando de graça (ver requireProductionCreditFeature em
 * backend/src/services/marketingDeliveryTypes.ts).
 */
/**
 * Feature cobrada quando o SUBFORMATO muda o consumo (espelha `subformatCreditFeature`
 * do backend). Tela dividida com cenas de IA gera as mesmas até 6 cenas do agente de
 * cena, então cobra como o agente de cena (spec §0).
 *
 * Mora fora do literal de DELIVERY_TYPES de propósito: o teste de espelho lê aquele
 * literal por regex, e um objeto aninhado lá dentro o quebraria.
 */
export const SUBFORMAT_CREDIT_FEATURE: Readonly<Record<string, Readonly<Record<string, string>>>> = Object.freeze({
  reaction_split: Object.freeze({ cenas_ia: 'video_cena' }),
});

export function productionFeatureKey(format?: string | null, videoEngine: VideoEngine = 'standard', subformat?: string | null): string | null {
  const key = normalizePipelineDeliveryType(format);
  const cfg = (DELIVERY_TYPES as Record<string, DeliveryTypeConfig>)[key]
    || (CREDIT_ALIASES[key] ? DELIVERY_TYPES[CREDIT_ALIASES[key]] : undefined);
  if (!cfg) return null;
  if (videoEngine === 'ultra' && cfg.ultraCreditFeature) return cfg.ultraCreditFeature;
  // Terceiro parâmetro OPCIONAL: toda chamada antiga continua devolvendo a mesma feature.
  const hit = subformat ? SUBFORMAT_CREDIT_FEATURE[key]?.[String(subformat).trim().toLowerCase()] : null;
  if (hit) return hit;
  return cfg.creditFeature;
}

/** Motor Ultra selecionável. Espelha `ultraEngine` do backend: video_agentico e narrado. */
export function supportsUltraEngine(format?: string | null): boolean {
  return !!deliveryConfig(format)?.ultraCreditFeature;
}

// ── Pipeline de cenas ────────────────────────────────────────────────────────

/**
 * Roda o pipeline de CENAS (takes) — espelho de isAgenticoPipelineDelivery do backend.
 *
 * Substitui o `deliveryType === 'video_agentico'` que sobrava em PostPreviewModal: a
 * regeração por cena da prévia é do MOTOR, não de um id de formato, e `narrado` usa o
 * mesmo motor. Consulta pelo id canônico (quem precisa normaliza antes), igual às demais.
 */
export function isAgenticoPipelineDelivery(deliveryType?: string | null): boolean {
  const cfg = (DELIVERY_TYPES as Record<string, DeliveryTypeConfig>)[String(deliveryType ?? '')];
  return !!cfg && cfg.agenticoPipeline;
}

// ── Subformatos de vídeo avatar (docs/NOVOS_FORMATOS_SPEC.md §3 e §8.3) ──────
//
// Espelho de AVATAR_SUBFORMATS / normalizeAvatarSubformat do backend. Subformato é
// CAMPO: `video_avatar` continua sendo `video_avatar` em todo lugar (menus, crédito,
// passo de overlay, render). O que muda é um bloco curto no prompt do brief.

export type AvatarSubformat = 'comum' | 'palestrinha' | 'storytelling_visual';
export const DEFAULT_AVATAR_SUBFORMAT: AvatarSubformat = 'comum';

export interface AvatarSubformatOption {
  value: AvatarSubformat;
  label: string;
  /** Uma linha, mostrada abaixo do rótulo: a MECÂNICA, não um slogan. */
  hint: string;
}

export const AVATAR_SUBFORMATS: AvatarSubformatOption[] = [
  { value: 'comum', label: 'Comum', hint: 'O vídeo avatar de sempre: os planos que o roteiro pedir.' },
  { value: 'palestrinha', label: 'Palestrinha', hint: 'Fala direta à câmera, um plano só do começo ao fim.' },
  { value: 'storytelling_visual', label: 'Storytelling visual', hint: 'A cena muda a cada trecho: looks e ambientes diferentes contextualizam a história.' },
];

/** O formato tem seleção de subformato. Hoje só vídeo avatar (resolve alias). */
export function supportsAvatarSubformat(format?: string | null): boolean {
  return !!deliveryConfig(format)?.avatarSubformats;
}

/** Desconhecido, vazio ou formato sem subformato ⇒ 'comum' (o comportamento de hoje). */
export function normalizeAvatarSubformat(value?: string | null, format?: string | null): AvatarSubformat {
  if (format !== undefined && !supportsAvatarSubformat(format)) return DEFAULT_AVATAR_SUBFORMAT;
  const key = String(value ?? '').trim().toLowerCase();
  return AVATAR_SUBFORMATS.some(o => o.value === key) ? (key as AvatarSubformat) : DEFAULT_AVATAR_SUBFORMAT;
}

// ── Subformato da TELA DIVIDIDA (docs/NOVOS_FORMATOS_SPEC.md §1 e §8.4) ──────
//
// Espelho de SPLIT_SUBFORMATS / normalizeSplitSubformat do backend. Subformato é CAMPO:
// o formato continua sendo `reaction_split` no menu, no render e no copy lock — o que
// muda é a FONTE da metade de cima (e, por consequência, a feature de crédito).

export type SplitSubformat = 'video_original' | 'cenas_ia';
export const DEFAULT_SPLIT_SUBFORMAT: SplitSubformat = 'video_original';

export interface SplitSubformatOption {
  value: SplitSubformat;
  label: string;
  /** Uma linha com a MECÂNICA, como nos subformatos de avatar. */
  hint: string;
}

export const SPLIT_SUBFORMATS: SplitSubformatOption[] = [
  { value: 'video_original', label: 'Usar vídeo original para reagir', hint: 'O vídeo do criador fica na metade de cima e o avatar reage embaixo.' },
  { value: 'cenas_ia', label: 'Criar animações com IA', hint: 'A metade de cima é cena gerada a partir do que a marca descreve; nenhum vídeo de terceiro.' },
];

/** O formato tem seleção de subformato de tela dividida. Hoje só `reaction_split`. */
export function supportsSplitSubformat(format?: string | null): boolean {
  return !!deliveryConfig(format)?.splitSubformats;
}

/** Desconhecido, vazio ou formato sem subformato ⇒ 'video_original' (o de hoje). */
export function normalizeSplitSubformat(value?: string | null, format?: string | null): SplitSubformat {
  if (format !== undefined && !supportsSplitSubformat(format)) return DEFAULT_SPLIT_SUBFORMAT;
  const key = String(value ?? '').trim().toLowerCase();
  return SPLIT_SUBFORMATS.some(o => o.value === key) ? (key as SplitSubformat) : DEFAULT_SPLIT_SUBFORMAT;
}

/**
 * Quais subformatos a ORIGEM do brief oferece (spec §1).
 *
 * Produto e marca não têm conteúdo de terceiro para reagir: ali existe só o lado IA, e
 * é por isso que `reaction_split` passa a aparecer nesses menus. Viral (reaction a post
 * real) e manual oferecem os dois — no viral o vídeo do criador é o ponto do formato.
 */
export function splitSubformatOptions(origin: 'viral' | 'produto' | 'marca' | 'manual'): SplitSubformatOption[] {
  return origin === 'produto' || origin === 'marca'
    ? SPLIT_SUBFORMATS.filter(o => o.value === 'cenas_ia')
    : SPLIT_SUBFORMATS;
}

/** O subformato inicial de cada origem: produto e marca já nascem no lado IA. */
export function defaultSplitSubformat(origin: 'viral' | 'produto' | 'marca' | 'manual'): SplitSubformat {
  return splitSubformatOptions(origin)[0].value;
}
